import { Injectable } from '@angular/core';
import { FunctionLifeCycle } from '../telephonyutils';
import { VoiceProvider } from '../telephony.provider';
import { Device, Call } from '@twilio/voice-sdk';
import { Logger } from '@app/core';
import { TelephonyService, TokenType } from '../telephony.service';
import { lastValueFrom } from 'rxjs';
import { TelephonyHelperService } from '../telephony.helper.service';
import { AuthenticationService } from '@app/core';
import { FormattedEmail } from '@app/core/helpers';
import { ToastService } from '../../../services/toast/toast.service';
import { Constants, EVENT_NAMES, EVENT_TYPES } from '@app/constants';
import { Conference } from '@app/core/types/conference';
import { LoaderService } from '@app/core/loader.service';
import { momentCustomInstance as moment } from '@projects/shared/src/app/utils/helper';
import { CallType } from '../../../features/call/interfaces/CallType/CallType';
import { AppConfigurationService } from '../../../services/app.configuration.service';

const { Codec } = Call;
const log = new Logger('VoiceProvider');

@Injectable({ providedIn: 'root' })
export class TwilioVoiceProvider extends VoiceProvider {
  private device!: Device;
  private worker!: any;
  private twilio_settings: any;
  private call?: Call | null;
  private conference?: Conference | null;
  private currentConference: string = '';
  private callerId = '';
  private currentCallSid: string = '';
  private thirdPartyCallSid: string = '';
  private primaryCampaign: any = {};
  private customerCallSid: string = '';
  private reservation: any;
  private isCallAnswered: boolean = false;
  private isCallDialed: boolean = false;
  private monitorDevice!: Device;
  private directDispose: boolean = false;
  private isConferenceLoaded: boolean = false;
  private leadId: string = '';
  private secondary_caller_ids?: Array<any> | null;
  constructor(
    public telephonyService: TelephonyService,
    public telephonyHelperService: TelephonyHelperService,
    public auth: AuthenticationService,
    public toast: ToastService,
    private loaderService: LoaderService,
    private appConfig: AppConfigurationService
  ) {
    super();
    this.events.subscribe((event) => {
      switch (event.type) {
        case 'Call':
          switch (event.name) {
            case 'abandoned':
              if (this.customerCallSid == event.data) {
                if (this.isCallDialed && !this.isCallAnswered) {
                  console.log(new Date().toLocaleString() + '  ➤  Call abandoned, removing current call events');
                  this.call?.disconnect();
                  this.call?.removeAllListeners();
                  this.call = null;
                  if (this.device) {
                    if (this.device.isBusy) {
                      console.log(new Date().toLocaleString() + '  ➤  Device got busy, disconnecting device');
                      this.device.disconnectAll();
                    }
                  }
                  this.resetState();
                  this.forceCompletePendingReservations(true);
                  this.events.next({ type: 'Call', name: 'close-modal', data: '' });
                }
              }
              break;
          }
          break;
        case EVENT_TYPES.OutboundDialer:
          switch (event.name) {
            case EVENT_NAMES.LeadSet:
              this.leadId = event.data;
              break;
            case EVENT_NAMES.ForceComplete:
              this.resetState();
              this.forceCompletePendingReservations(true);
              break;
          }
          break;
      }
    });
    this.auth.onlogout.subscribe((data: any) => {
      this.destroy({ onStart: () => {}, onEnd: () => {} });
    });
  }
  async init(lf: FunctionLifeCycle | null, campaign: any) {
    lf?.onStart();
    this.setCampaign(campaign);
    await this.fetchTwilioSettings();
    if (!this.auth.authData?.WorkerSid) {
      console.log(new Date().toLocaleString() + '  ➤  Worker and Device not initialized , worker sid not present');
      return;
    }
    const deviceResult = await this.setupDevice();
    if (deviceResult) {
      this.setupWorker();
    } else {
      console.log(new Date().toLocaleString() + '  ➤  Error in device setup, worker not initiated');
    }
    lf?.onEnd();
  }

  async fetchTwilioSettings() {
    log.info('Fetching twilio settings');
    const twilio_settings = localStorage.getItem('twilio_settings');
    if (twilio_settings == null) {
      const settings = await lastValueFrom(this.telephonyService.getTwilioSettings());
      this.twilio_settings = settings;
      localStorage.setItem('twilio_settings', JSON.stringify(settings));
    } else {
      this.twilio_settings = JSON.parse(twilio_settings);
    }
    return this.twilio_settings;
  }

  async setupWorker() {
    const response: any = await lastValueFrom(
      this.telephonyService.getToken(
        FormattedEmail(this.auth.authData.Username, false),
        TokenType.Worker,
        this.twilio_settings.AccountSid,
        this.auth.authData.WorkerSid
      )
    );
    this.worker = new window.Twilio.TaskRouter.Worker(response.Token, true, '', '', true, '', 28800);
    this.setupWorkerListeners();
    const defaultStatus = this.appConfig.getByIdentifier('DefaultStatus').Fields?.value;
    const statuses = this.appConfig.getByIdentifier('AgentStatuses').Fields;
    const isDefaultFound = statuses.find((x: any) => x.FriendlyName == defaultStatus);
    let status = '';
    if (!isDefaultFound && defaultStatus == Constants.STATUSES.LoggedIn.FriendlyName) {
      status = defaultStatus;
    } else {
      status = isDefaultFound ? defaultStatus : Constants.STATUSES.Wait.FriendlyName;
    }
    this.worker.fetch(async (error: any, result: any) => {
      if (error) {
        //log.error(`${this.auth.authData.Username} => Twilio fetch worker error`);
        this.toast.error('Unable to fetch agent. Please contact you supervisor');
      } else {
        if (result.activityName.toLowerCase() == 'offline') {
          this.updateWorkerActivity(status);
        } else if (result.activityName.toLowerCase() == 'busy') {
          this.updateWorkerActivity(status);
        } else {
          let lastStatus = localStorage.getItem('last_status');
          if (lastStatus) {
            this.updateWorkerActivity(Constants.STATUSES[result.activityName.replaceAll(' ', '')].FriendlyName);
          } else {
            this.updateWorkerActivity(status);
          }
        }
        this.forceCompletePendingReservations();
      }
    });
  }

  async setupDevice() {
    const hasMicrophone = await this.telephonyHelperService.checkMicrophone();
    if (!hasMicrophone) {
      log.info('Mic not enabled');
    }
    log.info('Initializing device');

    const response: any = await lastValueFrom(
      this.telephonyService.getToken(
        FormattedEmail(this.auth.authData.Username, false),
        TokenType.Voice,
        this.twilio_settings.AccountSid
      )
    );
    this.device = new Device(response.Token, {
      logLevel: 4,
      codecPreferences: [Codec.Opus, Codec.PCMU],
      closeProtection: false,
      dscp: true,
      edge: ['ashburn', 'umatilla'],
      tokenRefreshMs: 120000,
    });
    this.setupDeviceListeners();
    await this.device.register();
    return true;
  }

  async makeCall(
    lifeCycle: FunctionLifeCycle,
    to: string,
    callType: string = 'outbound',
    attributes: any = {},
    callBackTFN: any = '',
    callCampaign: any = {},
    countryCode: any = '',
    primary_call_sid: any = '',
    dialingForm: string = ''
  ) {
    if (!this.device) {
      this.call = null;
      this.toast.error('Device is not initialized!');
      throw new Error('Device is not initialized!');
    }
    if (this.device.state == Device.State.Unregistered) {
      this.call?.removeAllListeners();
      this.call = null;
      this.device.removeAllListeners();
      this.device.disconnectAll();
      this.device.destroy();
      await this.setupDevice();
    }
    if (this.device.isBusy) {
      this.call?.disconnect();
      this.call?.removeAllListeners();
      this.call = null;
      this.device.disconnectAll();
    }

    this.currentConference = `Flecx_${FormattedEmail(this.auth.authData.Username, false)}_${new Date().getTime()}`;
    let params: any = {
      ConferenceRoom: this.currentConference,
      To: to,
      CallerId: '',
      CustomerCallSid: '',
      LeadId: this.leadId,
    };
    let callerId = '';

    if (callType == 'outbound') {
      callerId = this.getCountryCallerId(attributes.country || countryCode);
      params.CallerId = callerId;

      this.isCallDialed = true;
    } else if (callType == 'callback') {
      callerId = callBackTFN;
      params.CallerId = callBackTFN;

      this.isCallDialed = true;
    } else {
      callerId = attributes.From;
      params.From = attributes.From;
      params.CustomerCallSid = attributes.CallSid;
      this.customerCallSid = attributes.CallSid;
      this.isCallDialed = false;
    }
    //* adding DialingType for where call is dialed
    params.DialingType = dialingForm;
    //checking callstatus for inbound call before dialing
    if (callType == 'inbound') {
     
      try {
        const activeCall: any = await lastValueFrom(
          this.telephonyService.fetchCall(attributes.CallSid, this.twilio_settings.AccountSid)
        );
        if (activeCall.Status != 'in-progress') {
          console.log(new Date().toLocaleString() + '  ➤  Call is not in progress, ending the call, Customer has left the call');
          this.toast.info('Customer has left the call.');
          this.loaderService.toggleMainLoader(false);
          this.call?.removeAllListeners();
          this.call = null;
          this.resetState();
          this.forceCompletePendingReservations(true);
          this.telephonyService.forceDisposedCall(attributes.CallSid, this.auth.authData.Id).subscribe((_) => {
           
          });
          this.events.next({ type: 'Call', name: 'close-modal', data: '' });
          return;
        }
      } catch (error) {
        console.log(new Date().toLocaleString() + '  ➤  Error in fetch call before dialing: ' + JSON.stringify(error));
        //log.error('Error in fetch call before dialing');
      }
    }
    this.isCallDialed = true;
    this.events.next({ type: 'Call', name: 'init', data: '' });
    let dialType = callType == 'outbound' || callType == 'callback' ? 'outbound' : 'inbound';

    try {
      const call = await this.device.connect({ params });
      this.call = call;
      call.on('messageReceived', (message) => {
        console.log(new Date().toLocaleString() + '  ➤  Call Message Received ::');
        let key = Object.keys(message.content)[0];
        if (key)
          switch (key) {
            case 'customerCallSid':
              this.customerCallSid = message.content.customerCallSid;
              localStorage.setItem('activeCallSid', this.customerCallSid);
              break;
            case 'call_event':
              if (message.content.call_event == 'customer_left') {
                this.events.next({ type: 'Call', name: 'customer_left', data: '' });
              }
              break;
            default:
          }
      });
      call.on('accept', async (...args: any) => {
        this.isCallAnswered = true;
        console.log(`Call Accepted `);
        let data: any = {};
        if (callType == 'outbound') {
          data = {
            campaignId: this.primaryCampaign.Id,
            campaignCode: this.primaryCampaign.Code,
            campaignName: this.primaryCampaign.Name,
            tfnCampaignId: this.primaryCampaign.Id,
            tfnCampaignCode: this.primaryCampaign.Code,
            tfnCampaignName: this.primaryCampaign.Name,
            btn: to,
            tfn: callerId,
            callSid: this.customerCallSid,
            callType: dialType,
            isCallBack: false,
            langCode: this.primaryCampaign.LangCode ? this.primaryCampaign.LangCode : 'en',
            leadId: this.leadId,
            primary_call_sid,
          };
        } else if (callType == 'callback') {
          data = {
            campaignId: callCampaign.Id,
            campaignCode: callCampaign.Code,
            campaignName: callCampaign.Name,
            tfnCampaignId: callCampaign.tfnCampaignId,
            tfnCampaignCode: callCampaign.tfnCampaignCode,
            tfnCampaignName: callCampaign.tfnCampaignName,
            btn: to,
            tfn: callerId,
            callSid: this.customerCallSid,
            callType: dialType,
            isCallBack: true,
            langCode: callCampaign.LanguageCode ? callCampaign.LanguageCode : 'en',
            leadId: this.leadId,
            primary_call_sid,
          };
        } else {
          console.log(new Date().toLocaleString() + '  ➤  attributes ==> ', attributes);
          let campaign = this.auth.authData.Campaigns.find((x: any) => x.Code == attributes.Campaign);
          this.customerCallSid = attributes.CallSid;
          data = {
            tfnCampaignId: attributes.TFNCampaignId,
            tfnCampaignCode: attributes.TFNCampaignCode,
            tfnCampaignName: attributes.TFNCampaignName,
            campaignId: campaign.Id,
            campaignCode: campaign.Code,
            campaignName: campaign.Name,
            btn: attributes.From,
            tfn: to,
            callSid: this.customerCallSid,
            callType: dialType,
            isCallBack: false,
            langCode: attributes.LangCode ? attributes.LangCode : 'en',
            leadId: this.leadId,
            primary_call_sid,
          };
          localStorage.setItem('activeCallSid', this.customerCallSid);
          this.customerCallSid = attributes.CallSid;
        }

        this.events.next({ type: 'Call', name: 'accept', data: data });
        this.toast.success('Call accepted');
        this.updateWorkerActivity(Constants.STATUSES.Busy.FriendlyName);
        this.currentCallSid = call.parameters['CallSid'];

        this.isConferenceLoaded = false;
        setTimeout(async () => {
          await this.getCallConference();
        }, 8000);

        //connected hangup case, checking call status if its still in-progress
        if (callType == 'inbound') {
          try {
            const activeCall: any = await lastValueFrom(
              this.telephonyService.fetchCall(this.customerCallSid, this.twilio_settings.AccountSid)
            );
            if (activeCall.Status != 'in-progress') {
              console.log(new Date().toLocaleString() + '  ➤  Call is not in progress, ending the call. Customer has left the call');
              this.toast.info('Customer has left the call');
              call.disconnect();
              this.loaderService.toggleMainLoader(false);
              this.events.next({ type: 'Call', name: 'close-modal', data: '' });
            }
          } catch (error) {
            console.log(new Date().toLocaleString() + '  ➤  Error in fetch call after dialing: ' + JSON.stringify(error));
            //log.error('Error in fetch call after dialing');
          }
        }
      });
      call.on('disconnect', (...args: any) => {
        console.log(`Call Disconnected `, this.isCallAnswered, this.isCallDialed, this.directDispose);
        this.toast.info('Call disconnected');
        if (this.isCallAnswered) {
          this.events.next({ type: 'Call', name: 'disconnect', data: 'args' });
          if (!this.directDispose) {
            console.log(`Marking status as wrap`);
            this.updateWorkerActivity(Constants.STATUSES.Wrap.FriendlyName);
          }
        } else if (this.isCallDialed && !this.isCallAnswered) {
          this.events.next({ type: 'Call', name: 'wrong-dialing', data: '' });
          if (this.leadId) {
            this.telephonyService.updateLeadStatus(this.leadId).subscribe((x) => console.log(new Date().toLocaleString() + '  ➤  lead status updated'));
            this.events.next({ type: EVENT_TYPES.OutboundDialer, name: EVENT_NAMES.FetchLead, data: '' });
          }
          this.resetState();
          this.forceCompletePendingReservations();
        }
        this.loaderService.toggleMainLoader(false);
      });
      call.on('cancel', (...args: any) => {
        console.log(`Call Cancel `);
      });
      call.on('error', (error) => {
        console.log(`Call Error `, error);
      });
    } catch (error) {
      if (callType == 'outbound') {
        this.events.next({ type: 'Call', name: 'failed', data: '' });
      }
      this.toast.error(
        'Cannot dial call. Please check you internet connection. If the problem persists refresh the page and try again'
      );
      this.loaderService.toggleMainLoader(false);
      this.call?.disconnect();
      this.call?.removeAllListeners();
      this.call = null;
    }
  }
  endCall(lifeCycle: FunctionLifeCycle, isDirect: boolean) {
    if (this.call) {
      if (isDirect) {
        this.directDispose = true;
      }
      this.call.disconnect();
      this.call.removeAllListeners();
      this.call = null;
    }
    this.events.next({ type: 'Call', name: 'disconnect', data: 'args' });
  }
  async disposeCall(lifecyle: FunctionLifeCycle, status: string) {
    this.events.next({ type: 'Call', name: 'dispose', data: { callSid: this.customerCallSid } });
    this.toast.success('Call ended');
    this.updateWorkerActivity(status, true);
  }
  async changeStatus(lifecyle: FunctionLifeCycle, status: string) {
    if (!this.worker) {
      this.toast.warning('Please wait..., Initializing agent');
      return;
    }
    const hasMicrophone = await this.telephonyHelperService.checkMicrophone();
    if (!hasMicrophone) {
      log.info('Mic not enabled');
      this.toast.info('Please enable microphone to get calls');
      return;
    }
    try {
      this.events.next({ type: 'Activity', name: 'init', data: '' });

      this.updateWorkerActivity(status);
    } catch (error) {
      //log.error(`${this.auth.authData.Username} => fetchWorkerActivities error`);
      this.toast.error(`Unable to change your status, Please try again`);
      this.events.next({ type: 'Activity', name: 'failed', data: '' });
    }
  }
  muteUnMuteCall(lifecyle: FunctionLifeCycle) {
    if (!this.call) {
      throw new Error('No call available');
    }
    if (this.call) {
      if (this.call.isMuted()) {
        this.call.mute(false);
      } else {
        this.call.mute(true);
      }
      this.events.next({ type: 'Call', name: 'mute', data: '' });
    }
  }
  async consultCall(lifecyle: FunctionLifeCycle, number: string, OnConference: boolean) {
    if (!this.isConferenceLoaded) {
      this.toast.info('Please wait ...');
      return;
    }
    if (!this.conference) {
      this.toast.info('No conference found');
      throw new Error('Conference not found');
    }
    this.events.next({ type: 'Consult', name: 'init', data: { callSid: this.customerCallSid } });

    this.telephonyService
      .consult(
        number,
        this.callerId,
        this.conference.Sid,
        this.customerCallSid,
        this.twilio_settings.AccountSid,
        OnConference
      )
      .subscribe({
        next: (response: any) => {
          this.events.next({ type: 'Consult', name: 'completed', data: '' });
          this.toast.success('Consultancy established successfully');
        },
        error: (err: any) => {
          if (!err.error?.title)
            this.toast.error('Consultancy failed. Please try again.');

          this.events.next({ type: 'Consult', name: 'failed', data: '' });
        },
      });
  }

  async conferenceCall(lifecyle: FunctionLifeCycle, number: string, OnConsult: boolean) {
    if (!this.isConferenceLoaded) {
      this.toast.info('Please wait ...');
      return;
    }
    if (!this.conference) {
      this.toast.info('No conference found');
      throw new Error('Conference not found');
    }

    this.events.next({ type: 'Conference', name: 'init', data: { callSid: this.customerCallSid } });

    this.telephonyService.conference(
      number,
      this.callerId,
      this.conference.Sid,
      this.customerCallSid,
      this.twilio_settings.AccountSid,
      OnConsult
    ).subscribe({
      next: (response: any) => {
        this.thirdPartyCallSid = response.Id;
        this.events.next({ type: 'Conference', name: 'completed', data: '' });
        this.toast.success('Conference call established successfully');
      },
      error: (err) => this.events.next({ type: 'Conference', name: 'failed', data: '' })
    });
  }
  async transferCall(lifecyle: FunctionLifeCycle, number: string, onConsultancy: boolean, onConference: boolean) {
    if (!this.isConferenceLoaded) {
      this.toast.info('Please wait ...');
      return;
    }
    if (!this.conference) {
      this.toast.info('No conference found');
      throw new Error('Conference not found');
    }
    this.events.next({ type: 'Transfer', name: 'init', data: '' });
    let request;
    if (onConsultancy || onConference) {
      request = this.telephonyService.warnTransferCall(
        this.conference.Sid,
        this.customerCallSid,
        this.twilio_settings.AccountSid,
        this.currentCallSid
      );
    } else {
      request = this.telephonyService.coldTransferCall(
        this.conference.Sid,
        this.customerCallSid,
        this.twilio_settings.AccountSid,
        this.currentCallSid,
        number
      );
    }

    request.subscribe({
      next: (response: any) => {
        this.events.next({ type: 'Transfer', name: 'completed', data: '' });
        this.toast.success('Call transfered successfully');
      },
      error: (err) => this.events.next({ type: 'Transfer', name: 'failed', data: '' })
    });
  }
  async endThirdPartyCall(lifecyle: FunctionLifeCycle, onConsultancy: boolean) {
    if (!this.isConferenceLoaded) {
      this.toast.info('Please wait ...');
      return;
    }
    if (!this.conference) {
      this.toast.info('No conference found');
      throw new Error('Conference not found');
    }

    this.events.next({ type: 'Cancel', name: 'init', data: '' });

    this.telephonyService.endThirdParty(
      this.conference.Sid,
      this.customerCallSid,
      this.twilio_settings.AccountSid,
      onConsultancy
    ).subscribe({
      next: (response: any) => {
        this.events.next({ type: 'Cancel', name: 'completed', data: '' });
        this.toast.success('Third party call ended successfully');
      },
      error: (err) => {
        const eventName = err.error?.title.toLower() === 'participant is not connected thirdparty' ?
        'completed' : 'failed';

        this.events.next({ type: 'Cancel', name: eventName, data: '' });
      }
    });
  }
  async toggleCall(lifecyle: FunctionLifeCycle, isCustomerConnected: boolean, onConsultancy: boolean) {
    if (!this.isConferenceLoaded) {
      this.toast.info('Please wait ...');
      return;
    }
    if (!this.conference) {
      this.toast.info('No conference found');
      throw new Error('Conference not found');
    }

    this.events.next({ type: 'Toggle', name: 'init', data: '' });
    isCustomerConnected = !isCustomerConnected;

    this.telephonyService.toggle(
      this.conference.Sid,
      this.customerCallSid,
      this.twilio_settings.AccountSid,
      isCustomerConnected,
      onConsultancy
    ).subscribe({
      next: (response: any) => {
        this.events.next({ type: 'Toggle', name: 'completed', data: isCustomerConnected });
        this.toast.success('Call toggled successfully');
      },
      error: (err) => this.events.next({ type: 'Toggle', name: 'failed', data: '' })
    });
  }
  async holdCall(lifecyle: FunctionLifeCycle, onHold: boolean) {
    if (!this.isConferenceLoaded) {
      this.toast.info('Please wait ...');
      return;
    }
    if (!this.conference) {
      this.toast.info('No conference found');
      throw new Error('Conference not found');
    }

    this.events.next({ type: 'Hold', name: 'init', data: '' });

    this.telephonyService
    .hold(
      this.conference.Sid,
      this.customerCallSid,
      this.currentCallSid,
      this.twilio_settings.AccountSid,
      !onHold
    )
    .subscribe({
      next: (response: any) => {
        this.events.next({ type: 'Hold', name: 'completed', data: '' });

        this.updateWorkerActivity(onHold ?
          Constants.STATUSES.Busy.FriendlyName :
          Constants.STATUSES.Hold.FriendlyName);

        this.toast.success(`Call ${onHold ? 'resumed' : 'hold'} successfully`);
      },
      error: (err) => {
        this.toast.error('Failed to update call status. Please try again.');
        this.events.next({ type: 'Hold', name: 'failed', data: '' });
      },
    });
  }
  destroy(lifecyle: FunctionLifeCycle) {
    this.updateWorkerActivity(Constants.STATUSES.Offline.FriendlyName);
    if (this.worker) {
      this.worker.removeAllListeners();
      this.worker = undefined;
    }
    if (this.device) {
      this.device.removeAllListeners();
      this.device.unregister();
      this.device.destroy();
    }
  }
  async acceptCall(lifecyle: FunctionLifeCycle) {
    this.events.next({ type: 'Reservation', name: 'reservation-init', data: '' });
    const acceptReservation = (reservation: any) => {
      return new Promise(async (resolve, reject) => {
        try {
          reservation.accept((error: any, reserv: any) => {
            if (error) {
              console.log(
                `${this.auth.authData.Username} => reservation.accept error = ${JSON.stringify(
                  error
                )}, Time =  ${new Date().toString()}`
              );
              console.log(error);
              console.log(error);
              reject(error);
              return;
            }
            resolve(reserv);
          });
        } catch (error) {
          reject(error);
        }
      });
    };
    try {
      await acceptReservation(this.reservation);
    } catch (error) {
      this.toast.error('Reservation cannot be accepted either it is canceled or assigned to other agent');
      this.events.next({ type: 'Reservation', name: 'error', data: '' });
      this.loaderService.toggleMainLoader(false);
      console.log(error);
    }
  }
  async rejectCall(lifecyle: FunctionLifeCycle) {
    this.events.next({ type: 'Reservation', name: 'reservation-init', data: '' });
    const rejectReservation = (reservation: any) => {
      return new Promise(async (resolve, reject) => {
        try {
          this.reservation.reject((error: any, reserv: any) => {
            if (error) {
              //log.error(`${this.auth.authData.Username} => reservation.reject error`);
              console.log(error);
              reject(error);
              return;
            }
            resolve(reserv);
          });
        } catch (error) {
          reject(error);
        }
      });
    };
    try {
      await rejectReservation(this.reservation);
    } catch (error) {
      this.events.next({ type: 'Reservation', name: 'error', data: '' });
      this.loaderService.toggleMainLoader(false);
      console.log(error);
    }
  }
  sendDTMF(lifecyle: FunctionLifeCycle, number: string) {
    if (this.call) {
      this.call.sendDigits(number);
    }
  }
  outboundCallHangup(lifecyle: FunctionLifeCycle, agentIdentity: string, errorMessage: string) {
    if (FormattedEmail(this.auth.authData.Username, false) == agentIdentity) {
      if (this.call) {
        this.call.disconnect();
        this.call = null;
        this.toast.warning(errorMessage);
      }
    }
  }
  async getQueuedCalls(
    lifecyle: FunctionLifeCycle,
    campaignCode: string,
    direction: number,
    selectedDialingCountry: string | null
  ) {
    const settings = await this.fetchTwilioSettings();
    const response: any = await lastValueFrom(
      this.telephonyService.getQueuedCalls(settings.AccountSid, campaignCode, direction, selectedDialingCountry)
    );
    return response;
  }
  async monitorCall(lifecyle: FunctionLifeCycle, customerCallSid: string) {
    if (this.device && this.device?.isBusy) {
      this.toast.error('Device is busy, Cannot monitor own call');
      return;
    }
    if (!this.twilio_settings) {
      await this.fetchTwilioSettings();
    }
    if (!this.monitorDevice) {
      const hasMicrophone = await this.telephonyHelperService.checkMicrophone();
      if (!hasMicrophone) {
        this.toast.error('Microphone not enabled');
        return;
      }
      this.events.next({ type: 'Monitor', name: 'init', data: customerCallSid });
      const response: any = await lastValueFrom(
        this.telephonyService.getToken(
          FormattedEmail(this.auth.authData.Username, false),
          TokenType.Voice,
          this.twilio_settings.AccountSid
        )
      );
      this.monitorDevice = new Device(response.Token, {
        logLevel: 4,
        codecPreferences: [Codec.Opus, Codec.PCMU],
      });
      this.monitorDevice.on('registered', (data) => {
        log.info('Twilio.Device Ready to monitor calls!');
      });
      await this.monitorDevice.register();
    }
    this.events.next({ type: 'Monitor', name: 'init', data: customerCallSid });
    const params: any = { IsCallMonitor: true, CustomerCallSid: customerCallSid };
    this.call = await this.monitorDevice.connect({ params });
    this.call.on('accept', (...args: any) => {
      this.events.next({ type: 'Monitor', name: 'accept', data: customerCallSid });
    });
    this.call.on('disconnect', (...args: any) => {
     this.events.next({ type: 'Monitor', name: 'disconnect', data: '' });
    });
    this.call.on('cancel', (...args: any) => {
      this.events.next({ type: 'Monitor', name: 'cancel', data: '' });
    });
  }
  async stopMonitorCall(lifecyle: FunctionLifeCycle) {
    if (this.call) {
      this.call.disconnect();
      this.call = null;
    }
  }
  async callMonitorOperation(lifecyle: FunctionLifeCycle, customerCallSid: string, monitorType: string) {
    let isWisper = false,
      isBarge = false;
    switch (monitorType) {
      case 'wisper':
        isWisper = true;
        isBarge = false;
        break;
      case 'barge':
        isWisper = false;
        isBarge = true;
        break;
    }
    this.events.next({ type: 'MonitorOperation', name: 'init', data: '' });
    this.telephonyService.monitorCall(
      customerCallSid,
      this.twilio_settings.AccountSid,
      isWisper,
      isBarge
    ).subscribe({
      next: (response: any) => this.events.next({ type: 'MonitorOperation', name: 'completed', data: monitorType }),
      error: (err) => this.events.next({ type: 'MonitorOperation', name: 'failed', data: '' })
    });
  }
  private setupWorkerListeners() {
    if (!this.worker) {
      this.toast.warning('Worker not initialized. Cannot bind events');
      throw new Error('TwilioTelephonyProvider:: Worker not initialized');
      return;
    }
    this.worker.on('ready', (worker: any) => {
      console.log(new Date().toLocaleString() + '  ➤  WORKER is READY :: ' + worker.sid);
    });
    this.worker.on('activity.update', (worker: any) => {
      console.log(`activity.update ${worker.activityName}`);
      setTimeout(() => {
        this.events.next({ type: 'Worker', name: 'update', data: worker.activityName });
      }, 500);
    });
    this.worker.on('attributes.update', (worker: any) => {
     
    });
    this.worker.on('capacity.update', function (channel: any) {
     
    });
    this.worker.on('reservation.created', async (reservation: any) => {
      this.events.next({ type: 'Reservation', name: 'created', data: '' });
      this.reservation = reservation;
      let campaign = this.auth.authData.Campaigns.find((x: any) => x.Code == reservation.task.attributes.Campaign);
      if (!campaign) {
        this.toast.info(`Campaign not assigned, Ask your supervisor to assign ${reservation.task.attributes.Campaign}`);
        reservation.reject();
        return;
      }
      console.log(reservation.task.attributes);
      const { task_type, lead_id, email, phone_number, From } = reservation.task.attributes;
      if (task_type.toLowerCase() == 'outbound' && this.primaryCampaign.OutboundDialerEnabled) {
        this.leadId = lead_id;
        this.events.next({
          type: EVENT_TYPES.OutboundDialer,
          name: EVENT_NAMES.Init,
          data: {
            campaignId: this.primaryCampaign.Id,
            lead_id,
            email,
            phone_number,
            direction: CallType.Outbound,
            type: 'reservation',
          },
        });
      } else if (this.primaryCampaign.OutboundDialerEnabled) {
        this.leadId = '';
        this.events.next({
          type: EVENT_TYPES.OutboundDialer,
          name: EVENT_NAMES.Init,
          data: {
            campaignId: this.primaryCampaign.Id,
            lead_id: '',
            email: '',
            phone_number: From,
            direction: CallType.Inbound,
            type: 'reservation',
          },
        });
      } else if (this.primaryCampaign.AutoAcceptCall) {
        this.toast.info('You are about to receive an incoming call');
        this.acceptCall({ onStart: () => {}, onEnd: () => {} });
      } else {
        this.toast.info('You are about to receive an incoming call');
        const attributes = reservation.task.attributes;
        this.events.next({
          type: 'Reservation',
          name: 'received',
          data: { callType: 'inbound', callSid: attributes.CallSid, btn: attributes.From },
        });
        this.telephonyHelperService.playIncomingCallAudio();
      }
    });
    this.worker.on('reservation.accepted', async (reservation: any) => {
      console.log(new Date().toLocaleString() + '  ➤  RESERVATION ACCEPTED');
      this.events.next({ type: 'Reservation', name: 'accepted', data: '' });
      const attributes = reservation.task.attributes;
      const callbacks: FunctionLifeCycle = { onStart: () => {}, onEnd: () => {} };
      console.log(new Date().toLocaleString() + '  ➤  Attributes => ', attributes);
      const { task_type, phone_number } = attributes;
      if (task_type.toLowerCase() == 'outbound' && this.primaryCampaign.OutboundDialerEnabled) {
        this.toast.success('Connecting to outbound call.');
        this.makeCall(callbacks, phone_number, 'outbound', attributes, '');
        this.events.next({ type: EVENT_TYPES.OutboundDialer, name: EVENT_NAMES.Started, data: {} });
      } else {
        this.toast.success('Connecting to inbound call.');
        this.makeCall(callbacks, attributes.To, 'inbound', attributes, '');
      }
      this.loaderService.toggleMainLoader(true);
      this.telephonyHelperService.pauseIncomingCallAudio();
      this.events.next({ type: EVENT_TYPES.Call, name: 'assign', data: attributes.CallSid });
    });
    this.worker.on('reservation.rejected', (reservation: any) => {
      console.log(new Date().toLocaleString() + '  ➤  RESERVATION REJECTED');
      this.toast.info('Your reservation has been rejected');
      this.events.next({ type: 'Reservation', name: 'rejected', data: '' });
      this.resetState();
    });
    this.worker.on('reservation.timeout', (reservation: any) => {
      console.log(new Date().toLocaleString() + '  ➤  RESERVATION TIMEOUT');
      this.events.next({ type: 'Reservation', name: 'timeout', data: '' });
      this.resetState();
      this.toast.warning('Your reservation has been timeout');
    });
    this.worker.on('reservation.canceled', (reservation: any) => {
      console.log(new Date().toLocaleString() + '  ➤  RESERVATION CANCELED');
      this.events.next({ type: 'Reservation', name: 'canceled', data: '' });
      this.toast.info('Your reservation has been canceled');
      this.resetState();
    });
    this.worker.on('reservation.rescinded', (reservation: any) => {
      console.log(new Date().toLocaleString() + '  ➤  RESERVATION rescinded');
      this.toast.info('Your reservation has been rescinded');
      this.events.next({ type: 'Reservation', name: 'rescinded', data: '' });
      this.resetState();
    });
    this.worker.on('token.expired', async () => {
      console.log(new Date().toLocaleString() + '  ➤  updating token');
      const response: any = await lastValueFrom(
        this.telephonyService.getToken(
          FormattedEmail(this.auth.authData.Username, false),
          TokenType.Worker,
          this.twilio_settings.AccountSid,
          this.auth.authData.WorkerSid
        )
      );
      this.worker.updateToken(response.Token);
    });
    this.worker.on('connected', () => {
      console.log(new Date().toLocaleString() + '  ➤  Websocket has connected');
    });
    this.worker.on('disconnected', () => {
      console.log(new Date().toLocaleString() + '  ➤  Websocket has disconnected');
      //this.events.next({ type: 'Worker', name: 'disconnected', data: '' });
    });
    this.worker.on('error', (error: any) => {
      console.log(new Date().toLocaleString() + '  ➤  Worker websocket had an error: ' + error.response + ' with message: ' + error.message);
    });
    this.worker.heartbeat.onsleep = () => {
      this.worker.heartbeat.now();
    };
    this.worker.heartbeat.onwakeup = () => {
     this.events.next({ type: 'Worker', name: 'wakeup', data: '' });
    };
    this.worker.heartbeat.now();
  }
  private setupDeviceListeners() {
    if (!this.device) {
      throw new Error('TwilioTelephonyProvider:: Device not initialized');
    }

    this.device.on('registered', (data) => {
      console.log(new Date().toLocaleString() + '  ➤  Twilio.Device Ready to make and receive calls!');
      this.events.next({ type: 'Device', name: 'registered', data: data });
    });

    this.device.on('tokenWillExpire', async () => {
      const response: any = await lastValueFrom(
        this.telephonyService.getToken(
          FormattedEmail(this.auth.authData.Username, false),
          TokenType.Voice,
          this.twilio_settings.AccountSid
        )
      );
      this.device.updateToken(response.Token);
    });

    this.device.on('error', (error: any) => {
      console.log(JSON.stringify(error));
      console.log(
        this.auth.authData.Username +
          ' device websocket had an error: ' +
          error.response +
          ' with message: ' +
          error.message
      );
      //this.events.next({ type: 'Device', name: 'error', data: error });
    });

    this.device?.audio?.on('deviceChange', (data: any) => {
      log.info('Device Changed');
      //this.events.next({ type: 'Device Audio', name: 'deviceChange', data: data });
    });
  }
  private async updateWorkerActivity(activity: string, isComplete: boolean = false) {
    if (!this.worker) {
      return;
    }
    let callSid = this.customerCallSid;
    const updateActivity = (activitySid: string) => {
      return new Promise((resolve, reject) => {
        this.worker.update('ActivitySid', activitySid, (error: any, updatedWorker: any) => {
          if (error) {
            //log.error(`${this.auth.authData.Username} updateActivity had an error`);
            reject(error);
          } else {
            resolve(updatedWorker);
          }
        });
      });
    };

    let status = Constants.STATUSES[activity];
    let response: any;
    let result: any;
    try {
      const activitySid = this.twilio_settings[(Constants.STATUSES as any)[activity]['Key']];
      this.events.next({ type: 'Activity', name: 'init', data: '' });
      let data = {
        Status: status.FriendlyName,
        UserId: this.auth.authData.Id,
        Version: '1.0.0',
        Campaigns: this.auth.authData.Campaigns?.map((x: any) => ({ Id: x.Id, Name: x.Name, Code: x.Code })),
        CallSid: callSid,
      };
      switch (status.FriendlyName) {
        case Constants.STATUSES.Hold.FriendlyName:
          response = await lastValueFrom(this.telephonyService.addUserStatusDB(data));
          this.events.next({
            type: 'Activity',
            name: 'update',
            data: { activity: status.FriendlyName, date: response.Id },
          });
          break;
        case Constants.STATUSES.Wrap.FriendlyName:
          let time = '';
          try {
            response = await lastValueFrom(this.telephonyService.addUserStatusDB(data));
            time = response.Id;
          } catch (error) {
            console.log(error);
            //log.error(` user status insert api failed/error`);
            time = moment().toISOString();
          }
          this.events.next({
            type: 'Activity',
            name: 'update',
            data: { activity: status.FriendlyName, date: time },
          });
          break;
        case Constants.STATUSES.LoggedIn.FriendlyName:
          response = await lastValueFrom(this.telephonyService.addUserStatusDB(data));
          this.events.next({
            type: 'Activity',
            name: 'update',
            data: { activity: status.FriendlyName, date: response.Id },
          });
          break;
        case Constants.STATUSES.Offline.FriendlyName:
          let twilio_res = await updateActivity(activitySid);
          break;
        default:
          let _time = '';
          try {
            response = await lastValueFrom(this.telephonyService.addUserStatusDB(data));
            _time = response.Id;
          } catch (error) {
            console.log(error);
            //log.error(` user status insert api failed/error`);
            _time = moment().toISOString();
          }
          this.events.next({
            type: 'Activity',
            name: 'update',
            data: { activity: status.FriendlyName, date: _time },
          });
          let twilio_ress = await updateActivity(activitySid);
          break;
      }
      if (isComplete) {
        if (this.reservation) {
          this.worker.completeTask(this.reservation.task.sid, (error: any) => {
            if (error) {
              //log.error(`${this.auth.authData.Username} => Twilio complete task error`);
              console.log(error);
              if (
                error.message.includes('because it is not currently assigned') ||
                error.message.includes('was not found')
              ) {
                console.log(
                  'Call already mark as completed or not currently assigned was marked by network disconnect'
                );
              }
            } else {
              
            }
          });
        }
        this.leadId = '';
        this.resetState();
      }
    } catch (error) {
      this.events.next({ type: 'Activity', name: 'failed', data: '' });
      this.toast.error('Cannot change status. Please try again');
      console.log(new Date().toLocaleString() + '  ➤  Error in updating worker activity');
      console.log(error);
    }
  }

  private async getCallConference() {
    if (!this.currentConference) {
      return;
    }
    try {
      const response: any = await lastValueFrom(
        this.telephonyService.getCallConference(this.twilio_settings.AccountSid, this.currentConference)
      );
      log.info('Conference found: ', response);
      this.conference = response;
      this.isConferenceLoaded = true;
      const cust = this.conference?.Participants.find((x: any) => x.Label === 'customer');
      if (cust) {
        this.customerCallSid = cust.CallSid;
        return this.customerCallSid;
      }
      this.events.next({ type: 'Call', name: 'conference', data: response?.Sid });
      return '';
    } catch (error) {
      console.log(new Date().toLocaleString() + '  ➤  Conference error');
      console.log(error);
      //log.error('Get all conference api error');
      this.isConferenceLoaded = true;
      return '';
    }
  }
  private resetState() {
    this.conference = null;
    this.currentConference = '';
    this.currentCallSid = '';
    this.customerCallSid = '';
    this.reservation = null;
    this.isCallDialed = false;
    this.isCallAnswered = false;
    this.call = null;
    localStorage.removeItem('activeCallSid');
    localStorage.removeItem('call_duration');
    this.telephonyHelperService.pauseIncomingCallAudio();
    this.directDispose = false;
    this.isConferenceLoaded = false;
    this.leadId = '';
  }
  public setCampaign(campaign: any) {
    this.callerId = campaign.CallerId;
    this.primaryCampaign = campaign;
    this.secondary_caller_ids = campaign.SecondaryCallerIds;
  }
  async forceCompletePendingReservations(isComplete: boolean = false) {
    if (!this.worker) {
      return;
    }
    const fetch_reservations = () => {
      return new Promise((resolve, reject) => {
        this.worker.fetchReservations(
          (error: any, reservations: any) => {
            if (error) {
              //log.error(`${this.auth.authData.Username} fetch_reservations had an error: ${error.message}`);
              console.log(error.code);
              console.log(error.message);
              reject(error);
            } else {
              resolve(reservations.data);
            }
          },
          { ReservationStatus: 'accepted' }
        );
      });
    };
    try {
      const data: any = await fetch_reservations();
      for (let i = 0; i < data.length; i++) {
        if (data[i].task.assignmentStatus === 'assigned') {
          if (isComplete) {
            console.log(new Date().toLocaleString() + '  ➤  Marking call as completed forcefully');
            data[i].task.complete();
          } else {
            if (!data[i].task.attributes.CallSid) {
              console.log(new Date().toLocaleString() + '  ➤  Marking call as completed forcefully');
              data[i].task.complete();
            } else {
              console.log(new Date().toLocaleString() + '  ➤  Dialing call again ');
              this.onReservationAccepted(data[i]);
            }
          }
        }
      }
    } catch (error) {
      console.log(error);
      console.log(new Date().toLocaleString() + '  ➤  Accepted reservation fetch failed');
    }
  }
  async sync(lifecyle: FunctionLifeCycle, status: any, lastStatus: any, onCall: any) {
    console.log(new Date().toLocaleString() + '  ➤  SYNCING STATUS');

    if (!this.worker) {
      console.log(new Date().toLocaleString() + '  ➤  Worker not initialized');
      return;
    }
    
    this.worker.heartbeat.now();
    const fetch_worker = () => {
      return new Promise((resolve: Function, reject: Function) => {
       
        this.worker.fetch((error: any, result: any) => {
          if (error) {
            //log.error(`${this.auth.authData.Username} fetch_worker had an error: ${error.message}`);
            reject(error);
          } else {
            resolve(result.activityName.toLowerCase());
          }
        });
      });
    };
    try {
      console.log(new Date().toLocaleString() + '  ➤  Syncing logic starts, fetching worker');
      if (onCall) {
        console.log(new Date().toLocaleString() + '  ➤  Syncing logic onCall, return');
        return;
      }
      const activityResult: any = await fetch_worker();
      const activity = activityResult;
      const current = status.FriendlyName.toLowerCase();
      console.log(new Date().toLocaleString() + '  ➤  Worker activity : ' + activity + ', Current activity : ' + current);

      if (activity.indexOf('busy') != -1) {
        if (current == 'wrap') {
          this.updateWorkerActivity(lastStatus.FriendlyName);
        } else if (current == 'wait') {
          console.log(new Date().toLocaleString() + '  ➤  Busy on twilio, forcefully updating activity on twilio to = ' + current);
          this.updateWorkerActivity(status.FriendlyName);
          this.forceCompletePendingReservations();
        }
      } else if (current == 'wait' && (activity == 'busy' || activity == 'break' || activity == 'manualcall')) {
        console.log(activity + ' on twilio, forcefully updating activity on twilio to = ' + current);
        this.updateWorkerActivity(status.FriendlyName);
        this.forceCompletePendingReservations();
      } else if (activity == 'wait' && current != activity) {
        console.log(activity + ' on twilio, forcefully updating activity on twilio to = ' + activity);
        this.updateWorkerActivity(status.FriendlyName);
        this.forceCompletePendingReservations();
      }
    } catch (error) {
      console.log(error);
      console.log(new Date().toLocaleString() + '  ➤  Syncing telephony failed');
    }
  }
  setOutboundCallSid(lifecyle: FunctionLifeCycle, callSid: string) {
    this.customerCallSid = callSid;
    localStorage.setItem('activeCallSid', this.customerCallSid);
  }
  closeExistingConnections(lifecyle: FunctionLifeCycle, callSid: string) {
    if (this.call) {
      this.call.disconnect();
      this.call.removeAllListeners();
      this.call = null;
    }
    if (this.worker) {
      this.worker.removeAllListeners();
      this.worker = undefined;
    }
    if (this.device) {
      this.device.removeAllListeners();
      this.device.unregister();
      this.device.destroy();
    }
  }
  getCountryCallerId(countryCode: string): string {
    let callerId = this.secondary_caller_ids?.find(
      (c) => c.CountryCode.toLowerCase() == countryCode.toLowerCase()
    )?.CallerId;
    if (!callerId) callerId = this.callerId;

    
    return callerId;
  }
  onReservationAccepted(reservation: any) {
    this.reservation = reservation;
    console.log(new Date().toLocaleString() + '  ➤  RESERVATION ACCEPTED 2nd');
    this.events.next({ type: 'Reservation', name: 'accepted', data: '' });
    const attributes = reservation.task.attributes;
    const callbacks: FunctionLifeCycle = { onStart: () => {}, onEnd: () => {} };
    console.log(new Date().toLocaleString() + '  ➤  Attributes => ', attributes);
    const { task_type, phone_number } = attributes;
    if (task_type.toLowerCase() == 'outbound' && this.primaryCampaign.OutboundDialerEnabled) {
      this.makeCall(callbacks, phone_number, 'outbound', attributes, '');
      this.events.next({ type: EVENT_TYPES.OutboundDialer, name: EVENT_NAMES.Started, data: {} });
    } else {
      this.makeCall(callbacks, attributes.To, 'inbound', attributes, '');
    }
    this.loaderService.toggleMainLoader(true);
    this.telephonyHelperService.pauseIncomingCallAudio();
    this.events.next({ type: EVENT_TYPES.Call, name: 'assign', data: attributes.CallSid });
  }
  reconnectVoice(lifecyle: FunctionLifeCycle | null) {
    
  }
 forceDisposedAndLogout(lifecyle: FunctionLifeCycle | null) {
    
  }
  reserveStatus(lifecyle: FunctionLifeCycle | null) {
    
  }
  async loadInsightSettings(lf: FunctionLifeCycle | null) {
    
  }
}
